/**
 * OmegaMemoryTile — Phase 15 dashboard tile.
 *
 * Shows the OMEGA memory store summary: total entries, breakdown by
 * type (lessons / precedents / facts / patterns), top tags, and the
 * most recent lessons. Polls every 30s via `useMemorySummary`.
 */

import { useMemorySummary } from '../hooks/useOmegaState';
import type { MemoryEntry } from '../data/types';

export function OmegaMemoryTile() {
  const { data, isPending, isError } = useMemorySummary();

  return (
    <section
      aria-labelledby="memory-heading"
      style={{
        background: 'var(--omega-surface)',
        border: '1px solid var(--omega-rim)',
        padding: 'var(--omega-gap-3)',
        marginTop: 'var(--omega-gap-3)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--omega-gap-2)',
          marginBottom: 'var(--omega-gap-2)',
        }}
      >
        <h2
          id="memory-heading"
          style={{
            margin: 0,
            fontSize: '11pt',
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'var(--omega-ink)',
          }}
        >
          OMEGA Memory
        </h2>
        {data && (
          <span
            style={{
              fontSize: '9pt',
              padding: '1px 7px',
              border: '1px solid var(--omega-rim-strong)',
              color: 'var(--omega-ink-muted)',
              letterSpacing: '0.06em',
              fontWeight: 600,
            }}
          >
            {data.total} entries
          </span>
        )}
      </div>

      {/* Body */}
      {isPending && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '10pt' }}>
          Loading…
        </div>
      )}

      {isError && (
        <div style={{ color: 'var(--omega-state-refused)', fontSize: '10pt' }}>
          Failed to load memory summary.
        </div>
      )}

      {!isPending && !isError && data && (
        <>
          {/* Type breakdown */}
          <div
            style={{
              display: 'flex',
              gap: 'var(--omega-gap-2)',
              flexWrap: 'wrap',
              marginBottom: 'var(--omega-gap-2)',
            }}
          >
            <TypeBadge label="Lessons" count={data.by_type.lesson} accent />
            <TypeBadge label="Precedents" count={data.by_type.precedent} />
            <TypeBadge label="Patterns" count={data.by_type.pattern} />
            <TypeBadge label="Facts" count={data.by_type.fact} />
          </div>

          {/* Top tags */}
          {data.top_tags.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: 6,
                flexWrap: 'wrap',
                marginBottom: 'var(--omega-gap-2)',
              }}
            >
              {data.top_tags.slice(0, 8).map((tag) => (
                <span
                  key={tag}
                  style={{
                    fontSize: '8pt',
                    padding: '1px 5px',
                    border: '1px solid var(--omega-rim)',
                    color: 'var(--omega-ink-subtle)',
                    letterSpacing: '0.04em',
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Recent lessons */}
          {data.recent_lessons.length > 0 && (
            <>
              <div
                style={{
                  fontSize: '9pt',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--omega-ink-subtle)',
                  marginBottom: 6,
                }}
              >
                Recent Lessons
              </div>
              {data.recent_lessons.slice(0, 3).map((entry) => (
                <LessonRow key={entry.memory_id} entry={entry} />
              ))}
            </>
          )}

          {data.total === 0 && (
            <div
              style={{
                color: 'var(--omega-ink-subtle)',
                fontSize: '10pt',
                letterSpacing: '0.04em',
                fontStyle: 'italic',
              }}
            >
              No memories stored yet.
            </div>
          )}
        </>
      )}
    </section>
  );
}

function TypeBadge({
  label,
  count,
  accent,
}: {
  label: string;
  count: number;
  accent?: boolean;
}) {
  const color = accent ? 'var(--omega-state-healthy)' : 'var(--omega-ink-muted)';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontSize: '9pt',
      }}
    >
      <span style={{ color, fontWeight: 700 }}>{count}</span>
      <span
        style={{
          color: 'var(--omega-ink-subtle)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
    </div>
  );
}

function LessonRow({ entry }: { entry: MemoryEntry }) {
  const question =
    typeof entry.content['question'] === 'string'
      ? (entry.content['question'] as string)
      : entry.memory_id;

  const confColor =
    entry.confidence === 'high'
      ? 'var(--omega-state-healthy)'
      : entry.confidence === 'medium'
        ? 'var(--omega-state-degraded)'
        : 'var(--omega-ink-subtle)';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--omega-gap-2)',
        padding: 'var(--omega-gap-1) 0',
        borderTop: '1px solid var(--omega-rim)',
      }}
    >
      <span
        style={{
          flexShrink: 0,
          fontSize: '8pt',
          padding: '1px 5px',
          border: `1px solid ${confColor}`,
          color: confColor,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontWeight: 600,
          marginTop: 2,
        }}
      >
        {entry.confidence}
      </span>
      <div
        style={{
          flex: 1,
          fontSize: '10pt',
          color: 'var(--omega-ink)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {question}
      </div>
      {entry.pinned && (
        <span
          style={{
            flexShrink: 0,
            fontSize: '8pt',
            color: 'var(--omega-state-degraded)',
            letterSpacing: '0.04em',
          }}
        >
          pinned
        </span>
      )}
    </div>
  );
}
